import React, { useCallback, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { Box, Card, ListItem, ListItemText } from "@mui/material";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import Container from "@mui/material/Container";
import List from "@mui/material/List";
import IconButton from "@mui/material/IconButton";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import { join, pluck } from "ramda";
import EditIcon from "@mui/icons-material/EditOutlined";
import { useDeleteUserOrganization } from "./userServices";
import AddOrganizationDialog from "./AddOrganizationDialog";
import EditUserOrganizationDialog from "./EditUserOrganizationDialog";
import ConfirmButton from "../components/ConfirmButton";

const useStyles = makeStyles(theme => ({
  root: {
    minWidth: 400,
    padding: theme.spacing(3),
    margin: theme.spacing(1),
  },

  topBar: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: theme.spacing(3),
  },

  newButton: {
    textTransform: "uppercase",
    color: "#127DA9",
    fontSize: "1.2rem",
    fontWeight: "bold",
  },

  listItem: {
    borderBottomColor: theme.palette.primary.dark,
    borderBottomStyle: "dashed",
    borderBottomWidth: 1,
  },
}));

const UserOrganizationsCard = ({ user, onAlert }) => {
  const classes = useStyles();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditEditDialogOpen] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState(null);

  const deleteOrganization = useDeleteUserOrganization();
  const handleDeleteOrganization = useCallback(
    async organizationId => {
      try {
        await deleteOrganization.mutateAsync({
          resourceIds: {
            userId: user.userId,
            organizationId,
          },
        });
      } catch (err) {
        onAlert("error", [err.message]);
      }
    },
    [deleteOrganization, user, onAlert],
  );

  const handleEditClicked = useCallback(organization => {
    setSelectedOrganization(organization);
    setEditEditDialogOpen(true);
  }, []);

  const handleAddSuccess = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const handleEditSuccess = useCallback(() => {
    setEditEditDialogOpen(false);
  }, []);

  const handleError = useCallback(
    error => {
      onAlert("error", [error.message]);
    },
    [onAlert],
  );

  return (
    <Card classes={{ root: classes.root }}>
      <AddOrganizationDialog
        open={dialogOpen}
        onCancel={useCallback(() => setDialogOpen(false), [])}
        onOk={handleAddSuccess}
        onError={handleError}
        user={user}
      />
      <EditUserOrganizationDialog
        open={editDialogOpen}
        onCancel={useCallback(() => setEditEditDialogOpen(false), [])}
        onOk={handleEditSuccess}
        onError={handleError}
        user={user}
        organization={selectedOrganization}
      />
      <Box className={classes.topBar}>
        <Box style={{ marginBottom: "10px" }}>
          <Typography variant="h2" color="primary" noWrap>
            User Organizations
          </Typography>
        </Box>

        <Grid container spacing={3} direction="row" justifyContent="flex-end">
          <Grid item>
            <Button
              variant={"text"}
              startIcon={<AddIcon />}
              classes={{ root: classes.newButton }}
              onClick={useCallback(() => setDialogOpen(true), [])}
            >
              Add User Organization
            </Button>
          </Grid>
        </Grid>
      </Box>
      <Container maxWidth={false}>
        <List dense={true}>
          {user.organizations.map(org => (
            <ListItem
              classes={{ root: classes.listItem }}
              key={org.organizationId}
            >
              <ListItemText
                primary={org.organizationName}
                secondary={org.organizationId}
              />
              <ListItemText
                primary={join(
                  ", ",
                  pluck("roleName", org.userOrganizationRoles),
                )}
              />
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  aria-label="delete"
                  onClick={() => handleEditClicked(org)}
                  size="large"
                >
                  <EditIcon />
                </IconButton>
                <ConfirmButton
                  handleDelete={() =>
                    handleDeleteOrganization(org.organizationId)
                  }
                  dialogTitle="Remove Property"
                  dialogBody="Are you sure you want to remove this property?"
                />
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </Container>
    </Card>
  );
};

export default UserOrganizationsCard;
